import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  GoogleAuthProvider,
  signInWithPopup,
  User as FirebaseUser,
  ActionCodeSettings
} from 'firebase/auth';
import { auth } from '../../firebase';
import { UserProfile } from '../types';

export const authService = {
  // Registrar nuevo usuario
  register: async (email: string, password: string) => {
    console.log('[AUTH] Iniciando registro para email:', email);
    try {
      console.log('[AUTH] Llamando a createUserWithEmailAndPassword');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('[AUTH] Usuario creado exitosamente, UID:', userCredential.user.uid);
      console.log('[AUTH] Email verificado:', userCredential.user.emailVerified);
      
      // Enviar email de verificación con configuración de acción
      console.log('[AUTH] Enviando email de verificación...');
      const actionCodeSettings: ActionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: false
      };
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      console.log('[AUTH] Email de verificación enviado exitosamente');
      
      return { 
        success: true, 
        user: userCredential.user,
        message: 'Registro exitoso. Por favor verifica tu correo electrónico.'
      };
    } catch (error: any) {
      console.error('[AUTH] Error en registro:', error);
      console.error('[AUTH] Error code:', error.code);
      console.error('[AUTH] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Iniciar sesión
  login: async (email: string, password: string) => {
    console.log('[AUTH] Iniciando login para email:', email);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('[AUTH] Login exitoso, UID:', userCredential.user.uid);
      
      // Verificar si el email está verificado
      if (!userCredential.user.emailVerified) {
        console.log('[AUTH] Email no verificado, rechazando login');
        return { 
          success: false, 
          error: 'Por favor verifica tu correo electrónico antes de iniciar sesión.',
          needsVerification: true
        };
      }
      console.log('[AUTH] Email verificado, login permitido');
      return { success: true, user: userCredential.user };
    } catch (error: any) {
      console.error('[AUTH] Error en login:', error);
      console.error('[AUTH] Error code:', error.code);
      console.error('[AUTH] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Cerrar sesión
  logout: async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Reenviar email de verificación
  resendVerificationEmail: async () => {
    console.log('[AUTH] Iniciando reenvío de email de verificación');
    try {
      const user = auth.currentUser;
      console.log('[AUTH] Usuario actual:', user ? user.uid : 'null');
      if (user) {
        console.log('[AUTH] Email del usuario:', user.email);
        console.log('[AUTH] Email verificado:', user.emailVerified);
        console.log('[AUTH] Enviando email de verificación...');
        const actionCodeSettings: ActionCodeSettings = {
          url: window.location.origin + '/login',
          handleCodeInApp: false
        };
        await sendEmailVerification(user, actionCodeSettings);
        console.log('[AUTH] Email de verificación enviado exitosamente');
        return { 
          success: true, 
          message: 'Email de verificación enviado exitosamente.' 
        };
      }
      console.error('[AUTH] No hay usuario autenticado');
      return { success: false, error: 'No hay usuario autenticado.' };
    } catch (error: any) {
      console.error('[AUTH] Error al reenviar email de verificación:', error);
      console.error('[AUTH] Error code:', error.code);
      console.error('[AUTH] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Enviar email de recuperación de contraseña
  resetPassword: async (email: string) => {
    console.log('[AUTH] Iniciando recuperación de contraseña para email:', email);
    try {
      console.log('[AUTH] Enviando email de recuperación de contraseña...');
      const actionCodeSettings: ActionCodeSettings = {
        url: window.location.origin + '/reset-password',
        handleCodeInApp: true
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      console.log('[AUTH] Email de recuperación de contraseña enviado exitosamente');
      return { 
        success: true, 
        message: 'Email de recuperación de contraseña enviado. Revisa tu bandeja de entrada.' 
      };
    } catch (error: any) {
      console.error('[AUTH] Error al enviar email de recuperación:', error);
      console.error('[AUTH] Error code:', error.code);
      console.error('[AUTH] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Verificar código de reset de contraseña
  verifyPasswordResetCode: async (code: string) => {
    console.log('[AUTH] Verificando código de reset de contraseña');
    try {
      const email = await verifyPasswordResetCode(auth, code);
      console.log('[AUTH] Código válido, email:', email);
      return { success: true, email };
    } catch (error: any) {
      console.error('[AUTH] Error al verificar código:', error);
      return { success: false, error: error.message };
    }
  },

  // Confirmar y aplicar nuevo password
  confirmPasswordReset: async (code: string, newPassword: string) => {
    console.log('[AUTH] Confirmando reset de contraseña');
    try {
      await confirmPasswordReset(auth, code, newPassword);
      console.log('[AUTH] Contraseña reseteada exitosamente');
      return { success: true, message: 'Contraseña actualizada exitosamente.' };
    } catch (error: any) {
      console.error('[AUTH] Error al confirmar reset:', error);
      return { success: false, error: error.message };
    }
  },

  // Iniciar sesión con Google
  loginWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      return { 
        success: true, 
        user: userCredential.user,
        message: 'Inicio de sesión con Google exitoso.'
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Observar cambios de autenticación
  onAuthStateChange: (callback: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(auth, callback);
  }
};
